import React from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import moment from 'moment';
import {
  Chip,
  ListItemText,
  Theme,
  createStyles,
  makeStyles
} from '@material-ui/core';
import {ToggleButton, ToggleButtonGroup} from '@material-ui/lab';
import {useTranslation} from 'react-i18next';
import {VaccineAppointmentSlotTypeTabs} from '@shared/constants/Mapping';
import {
  Patient,
  Vaccine
} from '@shared/services/service-mappers/AppointmentSchedulingMapper';
import Loader from '@components/framework/loader/Loader';
import HelperUtils, {getSlotType} from '@shared/utils/HelperUtils';
import SelectInputField from '@components/system/FormField/SelectInputField';
import AutocompleteInput from '@components/system/FormField/AutoCompleteInput';
import {
  APP_PRIMARY_COLOR,
  BORDER_PRIMARY_COLOR,
  CALENDAR_SELECTED_DAY_COLOR,
  DEFAULT_RED_COLOR,
  DEFAULT_WHITE_COLOR
} from 'src/style/variable';
import DaySlotTimeline, {
  IBusySlotItem,
  IDayWindow
} from './DaySlotTimeline/DaySlotTimeline';

const styles = makeStyles((theme: Theme) =>
  createStyles({
    appointmentSlotWrapper: {
      padding: theme.spacing(1, 0.5),
      overflowX: 'hidden',
      overflowY: 'auto',
      maxWidth: '100%',
      width: '100%',
      gap: theme.spacing(1.5),
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      minWidth: 0,
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(1.5, 1),
        gap: theme.spacing(1.75)
      }
    },
    topDropdownWrapper: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: theme.spacing(1),
      width: '100%',
      minWidth: 0,
      [theme.breakpoints.up('sm')]: {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: theme.spacing(1.25)
      },
      // 3-up only when desktop horizontal stepper is active (>=960)
      [theme.breakpoints.up('md')]: {
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))'
      }
    },
    fieldWrapper: {
      minWidth: 0,
      width: '100%',
      maxWidth: '100%'
    },
    slotsSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      flex: 1,
      minHeight: 0,
      minWidth: 0,
      width: '100%',
      maxWidth: '100%'
    },
    slotsHeaderRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      flexWrap: 'wrap',
      width: '100%',
      minWidth: 0
    },
    selectSlotTitle: {
      fontSize: '1.2rem',
      fontWeight: 600,
      margin: 0,
      minWidth: 0,
      flex: '1 1 auto',
      [theme.breakpoints.up('sm')]: {
        fontSize: '1.35rem'
      }
    },
    viewToggle: {
      border: `1px solid ${BORDER_PRIMARY_COLOR}`,
      borderRadius: 6,
      overflow: 'hidden',
      backgroundColor: DEFAULT_WHITE_COLOR,
      flex: '0 0 auto',
      maxWidth: '100%',
      '& .MuiToggleButton-root': {
        textTransform: 'none',
        padding: '0.5rem 0.9rem',
        fontSize: '0.9rem',
        fontWeight: 600,
        lineHeight: 1.2,
        border: 'none',
        borderRadius: 0,
        color: '#54585b',
        minWidth: '4.75rem'
      },
      '& .MuiToggleButton-root.Mui-selected': {
        backgroundColor: APP_PRIMARY_COLOR,
        color: DEFAULT_WHITE_COLOR,
        '&:hover': {
          backgroundColor: APP_PRIMARY_COLOR
        }
      },
      '& .MuiToggleButtonGroup-grouped:not(:first-child)': {
        marginLeft: 0,
        borderLeft: `1px solid ${BORDER_PRIMARY_COLOR}`
      }
    },
    slotCalendarWrapper: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
      alignItems: 'stretch',
      width: '100%',
      minWidth: 0,
      maxWidth: '100%',
      // Side-by-side only with desktop horizontal stepper (>= md / 960px)
      [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: theme.spacing(2)
      }
    },
    calenderWrapper: {
      // Fixed compact calendar — never stretch to fill the row
      width: '100% !important',
      maxWidth: '350px',
      flex: '0 0 auto',
      alignSelf: 'center',
      boxSizing: 'border-box',
      [theme.breakpoints.up('md')]: {
        width: '350px !important',
        maxWidth: '350px',
        flex: '0 0 350px',
        alignSelf: 'flex-start'
      },
      '& .holiday': {
        backgroundColor: DEFAULT_RED_COLOR,
        color: DEFAULT_WHITE_COLOR,
        '&:hover': {
          backgroundColor: DEFAULT_RED_COLOR,
          color: DEFAULT_WHITE_COLOR
        },
        '&.react-calendar__tile--active': {
          background: CALENDAR_SELECTED_DAY_COLOR,
          color: DEFAULT_WHITE_COLOR
        },
        '&.react-calendar__tile--active:hover': {
          background: CALENDAR_SELECTED_DAY_COLOR,
          color: DEFAULT_WHITE_COLOR
        }
      }
    },
    slotsWrapper: {
      flex: 1,
      minWidth: 0,
      width: '100%',
      maxWidth: '100%',
      minHeight: '12rem',
      maxHeight: 'min(55vh, 28rem)',
      overflowX: 'hidden',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.75),
      boxSizing: 'border-box',
      [theme.breakpoints.up('md')]: {
        minHeight: '20rem',
        maxHeight: 'min(62vh, 36rem)'
      },
      '@media (max-height: 720px)': {
        maxHeight: 'min(48vh, 22rem)'
      }
    },
    slotGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.75)
    },
    slotGroupTitle: {
      margin: 0,
      fontSize: '0.95rem',
      fontWeight: 600,
      color: '#54585b',
      paddingBottom: theme.spacing(0.25),
      borderBottom: `1px solid ${BORDER_PRIMARY_COLOR}`
    },
    chipsWrapper: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.65rem',
      alignContent: 'flex-start',
      maxWidth: '100%'
    },
    slotChip: {
      fontSize: '1rem',
      maxWidth: '100%',
      [theme.breakpoints.up('sm')]: {
        fontSize: '1.1rem'
      }
    },
    loaderWrapper: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '8rem'
    },
    emptyMessage: {
      margin: 0,
      color: '#54585b'
    }
  })
);

/* eslint-disable camelcase */
interface IVaccineSlotItem {
  start_time: string;
  end_time: string;
  duration: number;
}
const getSlotChipKey = (slot: IVaccineSlotItem, index: number): string =>
  `${slot.start_time}|${slot.end_time}|${index}`;
export interface IAppointmentType {
  id: number;
  name_id: string;
  label: string;
  is_provider_required?: boolean;
}

export interface IProvider {
  id: number;
  first_name: string;
  last_name: string;
}

export interface IHolidayItem {
  date: string;
  description?: string | null;
}

export interface ISelectedValue {
  appointmentDate: Date;
  slot: IVaccineSlotItem | null;
  vaccine: Vaccine | null;
  availableSlots: IVaccineSlotItem[];
  busySlots?: IBusySlotItem[];
  dayWindow?: IDayWindow | null;
  preferredTimeActiveTabIdx: number;
  loadingAppointmentSlots: boolean;
  patient: Patient | null;
  holidays: IHolidayItem[];
  appointmentType?: IAppointmentType | null;
  provider?: IProvider | null;
}
/* eslint-enable camelcase */

type SlotViewMode = 'list' | 'timeline';

const ScheduleAppointmentSlot = ({
  selectedValues,
  updateSelectedValues,
  patientsList,
  fetchPatients,
  availableVaccinesList,
  appointmentTypesList,
  availableProvidersList,
  isEditing = false,
  appointmentSlotDetails,
  hidePatientDropdown = false
}: {
  selectedValues: ISelectedValue;
  updateSelectedValues: React.Dispatch<React.SetStateAction<ISelectedValue>>;
  patientsList?: Patient[];
  fetchPatients?: (searchText: string) => Promise<Patient[] | undefined>;
  availableVaccinesList: Vaccine[];
  appointmentTypesList: IAppointmentType[];
  availableProvidersList: IProvider[];
  isEditing?: boolean;
  appointmentSlotDetails?: {
    startTime: string;
    endTime: string;
    date: string;
    duration: number;
    vaccineName: string;
    appointmentTypename: string;
    providerName: string;
  };
  /** When true, patient dropdown is hidden (e.g. patient preselected from patient table). */
  hidePatientDropdown?: boolean;
}): JSX.Element => {
  const classes = styles();
  const {t} = useTranslation();
  const [slotViewMode, setSlotViewMode] = React.useState<SlotViewMode>('list');

  const getValidationMessage = (): string => {
    if (!selectedValues.appointmentType) {
      return t(
        'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.NO_APPOINTMENT_TYPE_SELECTED'
      );
    }

    if (!selectedValues.patient) {
      return t(
        'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.NO_PATIENT_SELECTED'
      );
    }

    if (
      selectedValues.appointmentType.is_provider_required &&
      !selectedValues.provider
    ) {
      return t(
        'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.NO_PROVIDER_SELECTED'
      );
    }

    if (
      !selectedValues.appointmentType.is_provider_required &&
      !selectedValues.vaccine
    ) {
      return t(
        'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.NO_VACCINE_SELECTED'
      );
    }

    return t(
      'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.NO_APPOINTMENT_SLOTS_AVAILABLE'
    );
  };

  React.useEffect(() => {
    if (patientsList?.length === 1 && !isEditing) {
      updateSelectedValues(p => ({
        ...p,
        patient: patientsList[0],
        slot: null
      }));
    }
  }, [patientsList, isEditing, updateSelectedValues]);

  const changeSelectedAppointmentDateHandler = (date: Date) => {
    updateSelectedValues(p => ({...p, appointmentDate: date, slot: null}));
  };

  let tempSlots = [...selectedValues.availableSlots];
  if (
    appointmentSlotDetails?.date &&
    HelperUtils.formatDisplayDate(appointmentSlotDetails?.date) ===
      HelperUtils.formatDisplayDate(selectedValues.appointmentDate) &&
    appointmentSlotDetails?.startTime
  ) {
    const appointmentTime = new Date(
      '2000-01-01 ' + appointmentSlotDetails?.startTime
    );
    const appointmentTimeMillis = appointmentTime.getTime();

    let insertIndex;
    for (insertIndex = 0; insertIndex < tempSlots.length; insertIndex++) {
      const tempTime = new Date(
        '2000-01-01 ' + tempSlots[insertIndex].start_time
      );
      const tempTimeMillis = tempTime.getTime();

      if (tempTimeMillis >= appointmentTimeMillis) {
        break;
      }
    }

    tempSlots = [...tempSlots];

    tempSlots.splice(insertIndex, 0, {
      start_time: appointmentSlotDetails?.startTime,
      end_time: appointmentSlotDetails?.endTime,
      duration: appointmentSlotDetails?.duration
    });
  }

  const morningSlots = tempSlots.filter(
    slotItem => getSlotType(slotItem.start_time) === 'morning'
  );
  const afternoonSlots = tempSlots.filter(
    slotItem => getSlotType(slotItem.start_time) === 'afternoon'
  );

  const busySlots = selectedValues.busySlots || [];
  const hasListSlots = tempSlots.length > 0;
  const hasTimelineData =
    tempSlots.length > 0 ||
    busySlots.length > 0 ||
    !!selectedValues.dayWindow;

  let isSelectedDateHoliday = false;
  let selectedHolidayReason = '';
  if (selectedValues.appointmentDate) {
    const selectedDateKey = moment(selectedValues.appointmentDate).format(
      'YYYY-MM-DD'
    );
    const matchedHoliday = selectedValues.holidays.find(
      holiday => holiday.date === selectedDateKey
    );
    if (matchedHoliday) {
      isSelectedDateHoliday = true;
      selectedHolidayReason = (matchedHoliday.description || '').trim();
    }
  }

  const renderSlotChips = (
    slots: IVaccineSlotItem[],
    groupKey: string
  ): JSX.Element => (
    <div className={classes.chipsWrapper}>
      {slots.map((slotItem, index) => (
        <Chip
          key={`${groupKey}-${getSlotChipKey(slotItem, index)}`}
          className={classes.slotChip}
          label={`${slotItem.start_time} - ${slotItem.end_time}`}
          variant="outlined"
          onClick={() => {
            updateSelectedValues(p => ({
              ...p,
              slot: slotItem
            }));
          }}
          color={
            selectedValues.slot?.start_time === slotItem.start_time &&
            selectedValues.slot?.end_time === slotItem.end_time
              ? 'primary'
              : 'default'
          }
        />
      ))}
    </div>
  );

  return (
    <div className={classes.appointmentSlotWrapper}>
      <div className={classes.topDropdownWrapper}>
        <div className={classes.fieldWrapper}>
          <SelectInputField
            disabled={isEditing}
            label={t(
              'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.APPOINTMENT_TYPE'
            )}
            value={selectedValues?.appointmentType?.label}
            itemList={appointmentTypesList.map(i => ({
              label: i.label,
              value: i.label
            }))}
            noItemLabel={t(
              'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.NO_APPOINTMENT_TYPE'
            )}
            placeholder={
              !isEditing
                ? t(
                    'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.SELECT_APPOINTMENT_TYPE'
                  )
                : appointmentSlotDetails?.appointmentTypename
            }
            itemsHeaderLabel={t(
              'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.APPOINTMENT_TYPES'
            )}
            onClear={() => {
              updateSelectedValues(p => ({
                ...p,
                appointmentType: null,
                vaccine: null,
                slot: null,
                availableSlots: [],
                busySlots: [],
                dayWindow: null,
                provider: null
              }));
            }}
            showRadio
            onChange={e => {
              const updateValue = e.target.value;
              const selectedAppointmentType = appointmentTypesList.find(
                i => i.label === updateValue
              );
              if (selectedAppointmentType) {
                updateSelectedValues(p => ({
                  ...p,
                  appointmentType: selectedAppointmentType,
                  vaccine: null,
                  slot: null,
                  provider: null
                }));
              }
            }}
          />
        </div>
        {!hidePatientDropdown && (
          <div className={classes.fieldWrapper}>
            {/* eslint-disable camelcase */}
            <AutocompleteInput<{
              id?: number | undefined;
              is_appointment_patient?: boolean | undefined;
              first_name?: string | undefined;
              last_name?: string | undefined;
              middle_name?: string | undefined;
              dob?: string | undefined;
            }>
              // eslint-enable camelcase
              options={patientsList}
              label={t(
                'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.PATIENT'
              )}
              placeholder={t(
                'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.SELECT_PATIENT'
              )}
              value={selectedValues?.patient}
              getOptionSelected={(option: any, value: any) => {
                if (value) {
                  return option?.id === value?.id;
                } else {
                  return false;
                }
              }}
              getOptionLabel={(value: any) => {
                return HelperUtils.commaSeparatedFullName(
                  value?.first_name,
                  value?.last_name,
                  value?.middle_name
                );
              }}
              renderOption={(option: any) => (
                <ListItemText
                  primary={HelperUtils.commaSeparatedFullName(
                    option?.first_name,
                    option?.last_name,
                    option?.middle_name
                  )}
                  secondary={option?.emr_id}
                />
              )}
              onChange={(e, val) => {
                updateSelectedValues((p: any) => ({
                  ...p,
                  patient: val,
                  slot: null
                }));
              }}
              fetchOptions={fetchPatients}
            />
          </div>
        )}
        {selectedValues.appointmentType &&
          !selectedValues.appointmentType?.is_provider_required && (
            <div className={classes.fieldWrapper}>
              <SelectInputField
                disabled={isEditing}
                label={t(
                  'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.VACCINE'
                )}
                value={selectedValues?.vaccine?.proprietary_name}
                itemList={availableVaccinesList.map(i => ({
                  label: i.proprietary_name,
                  value: i.proprietary_name
                }))}
                noItemLabel="No Appointment"
                placeholder={
                  !isEditing
                    ? t(
                        'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.SELECT_VACCINE'
                      )
                    : appointmentSlotDetails?.vaccineName
                }
                itemsHeaderLabel={t(
                  'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.VACCINES'
                )}
                onClear={() => {
                  updateSelectedValues(p => ({
                    ...p,
                    vaccine: null,
                    slot: null,
                    availableSlots: [],
                    busySlots: [],
                    dayWindow: null
                  }));
                }}
                showRadio
                onChange={e => {
                  const updateValue = e.target.value;
                  const selectedVaccine = availableVaccinesList.find(
                    i => i.proprietary_name === updateValue
                  );
                  if (selectedVaccine) {
                    updateSelectedValues(p => ({
                      ...p,
                      vaccine: selectedVaccine,
                      slot: null
                    }));
                  }
                }}
              />
            </div>
          )}
        {selectedValues.appointmentType?.is_provider_required && (
          <div className={classes.fieldWrapper}>
            <SelectInputField
              disabled={isEditing}
              label={t(
                'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.PROVIDER'
              )}
              value={
                selectedValues.provider
                  ? `${selectedValues.provider.first_name} ${selectedValues.provider.last_name}`
                  : ''
              }
              itemList={availableProvidersList.map(p => ({
                label: `${p.first_name} ${p.last_name}`,
                value: `${p.first_name} ${p.last_name}`
              }))}
              noItemLabel={t(
                'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.NO_PROVIDER'
              )}
              placeholder={
                !isEditing
                  ? t(
                      'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.SELECT_PROVIDER'
                    )
                  : appointmentSlotDetails?.providerName
              }
              itemsHeaderLabel={t(
                'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.PROVIDERS'
              )}
              onClear={() => {
                updateSelectedValues(p => ({
                  ...p,
                  provider: null,
                  slot: null
                }));
              }}
              showRadio
              onChange={e => {
                const updateValue = e.target.value;
                const selectedProvider = availableProvidersList.find(
                  p => `${p.first_name} ${p.last_name}` === updateValue
                );
                if (selectedProvider) {
                  updateSelectedValues(p => ({
                    ...p,
                    provider: selectedProvider,
                    slot: null
                  }));
                }
              }}
            />
          </div>
        )}
      </div>
      <div className={classes.slotsSection}>
        <div className={classes.slotsHeaderRow}>
          <p className={classes.selectSlotTitle}>
            {t(
              'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.SELECT_AN_AVAILABLE_SLOT'
            )}
          </p>
          <ToggleButtonGroup
            className={classes.viewToggle}
            exclusive
            value={slotViewMode}
            onChange={(_e, value: SlotViewMode | null) => {
              if (value) {
                setSlotViewMode(value);
              }
            }}>
            <ToggleButton value="list">
              {t(
                'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.VIEW_LIST'
              )}
            </ToggleButton>
            <ToggleButton value="timeline">
              {t(
                'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.VIEW_TIMELINE'
              )}
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
        <div className={classes.slotCalendarWrapper}>
          <Calendar
            value={selectedValues.appointmentDate}
            onChange={changeSelectedAppointmentDateHandler}
            minDate={new Date()}
            className={classes.calenderWrapper}
            tileClassName={({date, view}) => {
              if (view === 'month' && selectedValues?.holidays?.length > 0) {
                const formattedDate = moment(date).format('YYYY-MM-DD');
                if (
                  selectedValues.holidays.some(
                    holiday => holiday.date === formattedDate
                  )
                )
                  return 'holiday';
              }
              return null;
            }}
          />
          <div className={classes.slotsWrapper}>
            {selectedValues.loadingAppointmentSlots && (
              <div className={classes.loaderWrapper}>
                <Loader />
              </div>
            )}
            {isSelectedDateHoliday && (
              <p className={classes.emptyMessage}>
                {selectedHolidayReason
                  ? t(
                      'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.SELECTED_DAY_IS_HOLIDAY_WITH_REASON',
                      {reason: selectedHolidayReason}
                    )
                  : t(
                      'APPOINTMENTS_SCHEDULAR_PORTAL.SELECT_APPOINTMENT_SLOT.SELECTED_DAY_IS_HOLIDAY'
                    )}
              </p>
            )}
            {!isSelectedDateHoliday &&
              !selectedValues.loadingAppointmentSlots &&
              slotViewMode === 'list' &&
              !hasListSlots && (
                <p className={classes.emptyMessage}>{getValidationMessage()}</p>
              )}
            {!isSelectedDateHoliday &&
              !selectedValues.loadingAppointmentSlots &&
              slotViewMode === 'timeline' &&
              !hasTimelineData && (
                <p className={classes.emptyMessage}>{getValidationMessage()}</p>
              )}
            {!selectedValues.loadingAppointmentSlots &&
              !isSelectedDateHoliday &&
              slotViewMode === 'list' &&
              hasListSlots && (
                <>
                  {morningSlots.length > 0 && (
                    <div className={classes.slotGroup}>
                      <p className={classes.slotGroupTitle}>
                        {t(VaccineAppointmentSlotTypeTabs[0].label)}
                      </p>
                      {renderSlotChips(morningSlots, 'morning')}
                    </div>
                  )}
                  {afternoonSlots.length > 0 && (
                    <div className={classes.slotGroup}>
                      <p className={classes.slotGroupTitle}>
                        {t(VaccineAppointmentSlotTypeTabs[1].label)}
                      </p>
                      {renderSlotChips(afternoonSlots, 'afternoon')}
                    </div>
                  )}
                </>
              )}
            {!selectedValues.loadingAppointmentSlots &&
              !isSelectedDateHoliday &&
              slotViewMode === 'timeline' &&
              hasTimelineData && (
                <DaySlotTimeline
                  availableSlots={tempSlots}
                  busySlots={busySlots}
                  dayWindow={selectedValues.dayWindow || null}
                  selectedSlot={selectedValues.slot}
                  onSelectSlot={slotItem => {
                    updateSelectedValues(p => ({
                      ...p,
                      slot: slotItem
                    }));
                  }}
                />
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleAppointmentSlot;
